import type { ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * The console's edit surface.
 *
 * Editing used to append a form below the table, which meant scrolling away from the row
 * being edited and losing the context of what else was there. A modal keeps the list in
 * view and puts the form where the eye already is.
 *
 * Built on shadcn's Dialog (Radix underneath), so focus trapping, Escape, scroll locking
 * and the aria wiring come from a maintained implementation rather than a hand-rolled
 * one. `size="lg"` exists for the lesson editor, whose block list is long enough that a
 * default-width dialog would be unusable.
 */
export function EditModal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  size?: 'md' | 'lg';
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto"
        // Width is an inline style, not a `sm:max-w-*` class, on purpose. Tailwind is
        // installed without Preflight (see app.css) so it cannot reset the design
        // system — but that partial install also emits no `--breakpoint-*`, so
        // responsive variants collapse into unconditional rules that lose to shadcn's
        // own `max-w-[calc(100%-2rem)]`. An explicit value is deterministic, and it
        // matches how the rest of this codebase sizes things.
        style={{ maxWidth: size === 'lg' ? 'min(960px, calc(100% - 2rem))' : 'min(620px, calc(100% - 2rem))' }}
      >
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'var(--font-display)', color: 'var(--text-strong)' }}>
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription style={{ color: 'var(--text-muted)' }}>{description}</DialogDescription>
          )}
        </DialogHeader>

        <div style={{ marginTop: 'var(--space-4)' }}>{children}</div>

        {footer && (
          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
              marginTop: 'var(--space-5)',
              paddingTop: 'var(--space-4)',
              borderTop: '1px solid var(--border-subtle)',
            }}
          >
            {footer}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
